import React, { useEffect, useRef } from 'react';
import { useController } from 'react-hook-form';
import clx from 'classnames';
import RDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { now, setHours, parseTimeString } from 'utils/time';
import useMedia from 'hooks/useMedia';
import { Portal } from 'react-portal';
import { FIELD_POPOVER_Z_INDEX } from 'utils/constants';

export enum Variant {
  Date = 'date',
  DateTime = 'datetime',
}

type AppProps = {
  label: string;
  caption?: string;
  name: string;
  placeholder?: string;
  isClearable?: boolean;
  className?: string;
  control: any;
  error?: string;
  required?: boolean;
  loading?: boolean;
  showPastDates?: boolean;
  showTimeSelect?: boolean;
  showFutureDates?: boolean;
  showTimeSelectOnly?: boolean;
  skipToday?: boolean;
  dateFormat?: string;
  height?: string;
  disabled?: boolean;
  readOnly?: boolean;
  range?: boolean;
  timeIntervals?: number;
  startTime?: string;
  endTime?: string;
};

const CalendarContainer = ({ children }: any) => {
  return (
    <Portal>
      {/* Portalled to body, so it competes with modal roots in the root stacking context —
          see FIELD_POPOVER_Z_INDEX. Set inline rather than as a Tailwind arbitrary value so
          the constant stays the single source of truth. */}
      <div
        className="w-full absolute"
        style={{ zIndex: FIELD_POPOVER_Z_INDEX }}
      >
        {children}
      </div>
    </Portal>
  );
};

const DatePicker: React.FC<AppProps> = ({
  name,
  label,
  control,
  caption,
  placeholder = 'Select Date/Time',
  isClearable = false,
  className = '',
  error = null,
  required = false,
  loading = false,
  showPastDates = false,
  showTimeSelect = true,
  showFutureDates = true,
  showTimeSelectOnly = false,
  skipToday = false,
  dateFormat = 'MM/dd/yyyy h:mm aa',
  height = 'h-10',
  readOnly = false,
  disabled = false,
  range = false,
  timeIntervals = 30,
  startTime,
  endTime,
}) => {
  const pickerRef = useRef<any>(null);
  const { isMobile } = useMedia();

  const { field } = useController({ name, control });

  // Filter function to skip today's date when skipToday is true
  const filterDate = (date: Date) => {
    if (!skipToday) return true;

    const today = new Date();
    const todayDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const selectedDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    return selectedDate.getTime() !== todayDate.getTime();
  };

  const inputStyle = clx(
    'w-full outline-none placeholder:text-text-light text-text text-sm rounded-lg focus:outline-none focus:ring-0',
    height,
    {
      'focus:border-error-dark border-error': !!error,
      'focus:border-primary border-border ': !error,
    }
  );

  useEffect(() => {
    if (isMobile && pickerRef.current !== null) {
      pickerRef.current.input.readOnly = true;
    }
  }, [isMobile, pickerRef]);

  return (
    <div className={`space-y-1 mb-2 ${className}`}>
      <div className="text-text-medium text-sm">
        {label}
        {required && <span className="text-error">*</span>}
      </div>
      <div className="text-xs text-text-lighter">{caption}</div>

      {readOnly ? (
        <div className="border border-border rounded-lg px-2 py-1.5">
          {field.value}
        </div>
      ) : (
        <RDatePicker
          {...field}
          // portalId="root-portal"
          ref={pickerRef}
          placeholderText={placeholder}
          className={inputStyle}
          name={name}
          selectsRange={range}
          autoComplete="off"
          selected={field.value}
          onChange={(d) => field.onChange(d)}
          showTimeSelectOnly={showTimeSelectOnly}
          showTimeSelect={showTimeSelect}
          autoFocus={false}
          preventOpenOnFocus
          minDate={showPastDates ? undefined : now()}
          maxDate={showFutureDates ? undefined : now()}
          minTime={startTime ? parseTimeString(startTime) : setHours(7)}
          maxTime={endTime ? parseTimeString(endTime) : setHours(20)}
          timeFormat="hh:mm a"
          timeIntervals={timeIntervals}
          disabled={loading || disabled}
          isClearable={isClearable}
          showPopperArrow
          clearButtonClassName="h-8 w-8 bg-yellow-100"
          dateFormat={dateFormat}
          popperContainer={CalendarContainer}
          filterDate={filterDate}
        />
      )}
      {error && <div className="text-error text-xs">*{error}</div>}
    </div>
  );
};

DatePicker.displayName = 'DatePicker';

export default DatePicker;
